import React from 'react';
import { Selection, Pedido } from '../../../../types';
import { formatCurrency } from '../../../../../../../../../utils/functions';
import QuantitySelector from '../../../shared/QuantitySelector/QuantitySelector';
import ClientSelector from '../ClientSelector/ClientSelector';
import ItemInfo from '../../../shared/ItemInfo/ItemInfo';
import ActionButtons from '../../../shared/ActionButtons/ActionButtons';
import ActionMenu from '../../../shared/ActionMenu/ActionMenu';
import './SelectionRow.scss';

interface SelectionRowProps {
  selection: Selection & { pedido: Pedido };
  allowEdit: boolean;
  activeDropdownId: string | null;
  onIncreaseQuantity: (pedido_id: number, selection_id: string) => void;
  onDecreaseQuantity: (pedido_id: number, selection_id: string) => void;
  onDeleteSelection: (pedido_id: number, selection_id: string) => void;
  onSplitSelection?: (pedido_id: number, selection_id: string) => void;
  onToggleDropdown: (dropdownId: string, event: React.MouseEvent) => void;
  onSelectClient: (pedidoId: number, selectionId: string, clientIndex: number) => void;
  numPeople: number;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (pedido_id: number, selection_id: string) => void;
}

const SelectionRow: React.FC<SelectionRowProps> = ({
  selection,
  allowEdit,
  activeDropdownId,
  onIncreaseQuantity,
  onDecreaseQuantity,
  onDeleteSelection,
  onSplitSelection,
  onToggleDropdown,
  onSelectClient,
  numPeople,
  isSelectionMode = false,
  isSelected = false,
  onToggleSelection
}) => {
  const dropdownId = `${selection.pedido.pedido_id}-${selection.selection_id}`;
  const isDropdownActive = activeDropdownId === dropdownId;
  const isPayed = selection.payed === true;

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Prevent selection if already payed
    if (isPayed) return;
    
    if (onToggleSelection) {
      onToggleSelection(selection.pedido.pedido_id, selection.selection_id);
    }
  };

  return (
  <tr className={`selection-row ${isDropdownActive ? 'dropdown-active' : ''} ${isSelectionMode ? 'selection-mode' : ''} ${isPayed ? 'payed-selection' : ''} ${isSelected ? 'selected' : ''}`}>
      {/* Quantity Selector or Checkbox Column */}
      <td className="quantity-column">
        {isSelectionMode ? (
          <div className="checkbox-container" onClick={handleCheckboxClick}>
            <input 
              type="checkbox" 
              checked={isSelected}
              onChange={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className="selection-checkbox"
              disabled={isPayed}
              readOnly
            />
          </div>
        ) : (
          <QuantitySelector
            quantity={selection.quantity}
            allowEdit={allowEdit}
            itemName={selection.item_name}
            onIncrease={() => onIncreaseQuantity(selection.pedido.pedido_id, selection.selection_id)}
            onDecrease={() => onDecreaseQuantity(selection.pedido.pedido_id, selection.selection_id)}
            isGranel={selection.is_granel}
            unitAcronym={selection.unit_acronym}
          />
        )}
      </td>

      {/* Client Selector Column */}
      <td className="client-column">
        {!isSelectionMode && (
          <ClientSelector
            clientIndex={selection.clientIndex}
            dropdownId={dropdownId}
            activeDropdownId={activeDropdownId}
            onToggleDropdown={onToggleDropdown}
            onSelectClient={(clientIndex) => onSelectClient(selection.pedido.pedido_id, selection.selection_id, clientIndex)}
            numPeople={numPeople}
          />
        )}
      </td>

      {/* Item Info Column (price inline) */}
      <td className="item-column">
        <ItemInfo selection={selection} priceForMobile={formatCurrency(selection.total_price)} />
      </td>

      {/* Action Buttons Column */}
      <td className="actions-column">
        {!isSelectionMode && (
          <>
            {/* Desktop: Individual action buttons */}
            <div className="desktop-actions">
              <ActionButtons
                quantity={selection.quantity}
                onSplit={() => onSplitSelection?.(selection.pedido.pedido_id, selection.selection_id)}
                onDelete={() => onDeleteSelection(selection.pedido.pedido_id, selection.selection_id)}
                allowEdit={allowEdit}
              />
            </div>
            {/* Mobile: Three-dot menu */}
            <div className="mobile-actions">
              <ActionMenu
                quantity={selection.quantity}
                onSplit={() => onSplitSelection?.(selection.pedido.pedido_id, selection.selection_id)}
                onDelete={() => onDeleteSelection(selection.pedido.pedido_id, selection.selection_id)}
                allowEdit={allowEdit}
              />
            </div>
          </>
        )}
      </td>
    </tr>
  );
};

export default SelectionRow;
